import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Avatar } from '../components/Avatar'
import { userApi, analyticsApi, subscriptionApi } from '../services/api'
import SubscriptionManagement from '../components/SubscriptionManagement'
import { useQuery } from '@tanstack/react-query'
import { TIER_FEATURES, SubscriptionTier, Subscription } from '@treu/shared'

const ProfilePage: React.FC = () => {
  const { logout, user, uploadAvatar } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // Check if user has analytics access based on tier
  const userTier = (user?.subscriptionTier || 'free') as SubscriptionTier
  const hasAnalyticsAccess = TIER_FEATURES[userTier]?.analytics ?? false

  // Get analytics data
  const { data: analyticsData, isLoading: analyticsLoading } = useQuery({
    queryKey: ['analytics', user?.id],
    queryFn: () => analyticsApi.getAnalytics(),
    enabled: !!user,
    staleTime: 0, // Never use cached data - always fetch fresh
  })

  // Start at the top on a plain /profile visit. `#upgrade` is handled by
  // SubscriptionManagement, which owns the plan grid and knows when it exists.
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0)
    }
  }, [location.hash])

  // Handle mobile app deep link redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const isMobile = params.get('mobile') === 'true'
    const subscriptionStatus = params.get('subscription')

    if (isMobile && subscriptionStatus) {
      const deepLink = `treu://subscription/${subscriptionStatus}`

      // Try immediate redirect
      window.location.replace(deepLink)

      // Fallback: Add meta refresh tag for browsers that block JS redirects
      const meta = document.createElement('meta')
      meta.httpEquiv = 'refresh'
      meta.content = `0;url=${deepLink}`
      document.head.appendChild(meta)
    }
  }, [])

  const [isExporting, setIsExporting] = useState(false)
  const [exportSuccess, setExportSuccess] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletionSubscription, setDeletionSubscription] = useState<Subscription | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null)
  const [showImageCropper, setShowImageCropper] = useState(false)
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null)
  const [cropPosition, setCropPosition] = useState({ x: 50, y: 50 })
  const [isSavingAvatar, setIsSavingAvatar] = useState(false)
  const [avatarError, setAvatarError] = useState<string | null>(null)
  // Get user wisdom level
  const getUserWisdomLevel = () => {
    const verifications = user?.monthlyChecksUsed || user?.trialChecksUsed || 0
    if (verifications >= 500) return { name: 'Wisdom Curator' }
    if (verifications >= 201) return { name: 'Discerning Mind' }
    if (verifications >= 51) return { name: 'Critical Thinker' }
    return { name: 'Curious Observer' }
  }

  const wisdomLevel = getUserWisdomLevel()

  const handleDataExport = async () => {
    setIsExporting(true)
    setExportError(null)
    try {
      const { blob, filename } = await userApi.exportData()

      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      setExportSuccess(true)
      setTimeout(() => setExportSuccess(false), 3000)
    } catch (error) {
      console.error('Export failed:', error)
      const axiosError = error as { response?: { status?: number } }
      if (axiosError?.response?.status === 401) {
        setExportError('Your session has expired. Please log in again.')
      } else {
        setExportError('Failed to export data. Please try again or contact support if the problem persists.')
      }
    } finally {
      setIsExporting(false)
    }
  }

  const handleDeleteAccount = async () => {
    // Check if user is logged in
    if (!user) {
      setDeleteError('User information not available. Please refresh the page and try again.')
      return
    }

    // Fetch subscription info to show a warning if they have an active paid plan.
    // Best-effort — failure must not block the modal from opening.
    try {
      const sub = await subscriptionApi.getSubscription()
      setDeletionSubscription(sub)
    } catch {
      setDeletionSubscription(null)
    }

    setShowDeleteModal(true)
  }

  const confirmDeleteAccount = async () => {
    setIsDeleting(true)
    setDeleteError(null)

    try {
      const response = await userApi.deleteAccount()

      // Account deleted successfully - show success message then logout
      setDeleteSuccess(response.message || 'Account deleted successfully.')
      setTimeout(async () => {
        await logout()
      }, 2000)

    } catch (error) {
      console.error('Account deletion error:', error)

      // Type guard for axios-like errors
      const axiosError = error as { response?: { status?: number; data?: { error?: string } } }

      // Provide more specific error messages
      if (axiosError?.response?.status === 401) {
        setDeleteError('Authentication failed. Please log in again and try deleting your account.')
        setTimeout(async () => {
          await logout()
        }, 2000)
      } else if (axiosError?.response?.data?.error) {
        setDeleteError(`Failed to delete account: ${axiosError.response.data.error}`)
      } else {
        setDeleteError('Failed to delete account. Please try again or contact support if the problem persists.')
      }
    } finally {
      setIsDeleting(false)
    }
  }

  const cancelDeleteAccount = () => {
    setShowDeleteModal(false)
    setDeleteError(null)
    setDeleteSuccess(null)
    setDeletionSubscription(null)
  }

  const handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        setTempImageUrl(e.target?.result as string)
        setCropPosition({ x: 50, y: 50 }) // Reset to center
        setShowImageCropper(true)
      }
      reader.readAsDataURL(file)
    }
    // Reset the input so the same file can be selected again
    event.target.value = ''
  }

  const handleCropSave = async () => {
    if (!tempImageUrl) return

    setIsSavingAvatar(true)
    setAvatarError(null)
    try {

      // Convert data URL to File object for upload
      const response = await fetch(tempImageUrl)
      const blob = await response.blob()
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' })

      // Use the useUser hook's uploadAvatar function
      await uploadAvatar(file)


      // Close modal - the useUser hook automatically updates the cache and localStorage
      setShowImageCropper(false)
      setTempImageUrl(null)

    } catch (error) {
      console.error('Failed to upload avatar:', error)
      setAvatarError((error as Error).message || 'Failed to save avatar. Please try again.')
    } finally {
      setIsSavingAvatar(false)
    }
  }

  const handleCropCancel = () => {
    setShowImageCropper(false)
    setTempImageUrl(null)
    setCropPosition({ x: 50, y: 50 })
  }

  return (
    <div className="min-h-screen bg-paper-0 px-4 py-6 sm:px-6">
      {/* Page Heading */}
      <div className="mb-8 border-b border-paper-200 pb-4">
        <p className="mb-2 font-display text-xs font-medium uppercase tracking-[0.25em] text-accent-600">
          Account
        </p>
        <h1 className="text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
          My Profile
        </h1>
        <p className="mt-2 text-base text-ink-600">
          Manage your account settings and verification preferences
        </p>
      </div>

      {/* Avatar Error Message */}
      {avatarError && (
        <div className="mb-8 rounded-md border border-danger-500/30 bg-danger-100 p-4 text-center text-danger-500">
          {avatarError}
        </div>
      )}

      {/* Profile Header */}
      <div className="mb-8 rounded-lg border border-paper-200 bg-white p-6 shadow-card sm:p-8">
        <div className="flex items-center">
          <div className="relative mr-6 shrink-0">
            <Avatar
              size="large"
              onClick={() => document.getElementById('avatar-upload')?.click()}
            />
            <div
              className="absolute -bottom-0.5 -right-0.5 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border-2 border-white bg-brand-950 text-[10px] text-white"
              onClick={() => document.getElementById('avatar-upload')?.click()}
            >
              +
            </div>
            <input
              id="avatar-upload"
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="sr-only"
            />
          </div>
          <div className="min-w-0">
            <h2 className="mb-1 truncate text-xl font-semibold text-ink-900 sm:text-2xl">
              {user?.name || user?.email?.split('@')[0] || 'User'}
            </h2>
            <div className="mb-1">
              <span className="text-base font-medium text-accent-600">
                {wisdomLevel.name}
              </span>
            </div>
            <p className="truncate text-sm text-ink-400">
              {user?.email || 'user@example.com'}
            </p>
          </div>
        </div>
      </div>


      {/* Stats Cards */}
      <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        <div className="rounded-lg border border-paper-200 bg-white p-6 shadow-card">
          <div className="mb-3 font-display text-xs font-medium uppercase tracking-[0.25em] text-accent-600">
            Verifications
          </div>
          <div className="mb-2 flex items-baseline gap-2">
            <span className="font-display text-4xl font-semibold text-ink-900">
              {analyticsData?.data?.stats?.totalVerifications || 0}
            </span>
            <span className="text-sm text-accent-600">
              {analyticsData?.data?.stats?.recentActivity ? `+${analyticsData.data.stats.recentActivity} this week` : 'Get started!'}
            </span>
          </div>
          <div className="text-sm text-ink-600">
            Your commitment to truth verification builds credibility in your network.
          </div>
        </div>

        <div className="relative overflow-hidden rounded-lg border border-paper-200 bg-white p-6 shadow-card">
          <div className="mb-3 font-display text-xs font-medium uppercase tracking-[0.25em] text-accent-600">
            Trust Score
          </div>
          {hasAnalyticsAccess ? (
            <>
              <div className="mb-2 flex items-baseline gap-2">
                <span className="font-display text-4xl font-semibold text-ink-900">
                  {analyticsLoading ? '...' : analyticsData?.data?.trustScore || 0}
                </span>
                <span className="text-sm text-ink-400">
                  out of 100
                </span>
              </div>
              <div className="text-sm text-ink-600">
                Based on verification accuracy, consistency, and community reputation.
              </div>
            </>
          ) : (
            <>
              <div className="mb-2 flex items-baseline gap-2 opacity-50 blur">
                <span className="font-display text-4xl font-semibold text-ink-900">
                  85
                </span>
                <span className="text-sm text-ink-400">
                  out of 100
                </span>
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center rounded-lg bg-white/85 p-4 backdrop-blur-sm">
                <span className="mb-3 text-center text-sm text-ink-600">
                  Track your trust score with Premium or higher
                </span>
                <button
                  onClick={() => navigate('/profile#upgrade')}
                  className="min-h-[44px] rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent-600"
                >
                  Upgrade to Unlock
                </button>
              </div>
            </>
          )}
        </div>

        <div className="relative overflow-hidden rounded-lg border border-paper-200 bg-white p-6 shadow-card">
          <div className="mb-3 font-display text-xs font-medium uppercase tracking-[0.25em] text-accent-600">
            Network Impact
          </div>
          {hasAnalyticsAccess ? (
            <>
              <div className="mb-2 flex items-baseline gap-2">
                <span className="font-display text-4xl font-semibold text-ink-900">
                  {analyticsLoading ? '...' : analyticsData?.data?.networkImpact || 0}
                </span>
                <span className="text-sm text-ink-400">
                  influence score
                </span>
              </div>
              <div className="text-sm text-ink-600">
                Your influence on information accuracy across the community network.
              </div>
            </>
          ) : (
            <>
              <div className="mb-2 flex items-baseline gap-2 opacity-50 blur">
                <span className="font-display text-4xl font-semibold text-ink-900">
                  72
                </span>
                <span className="text-sm text-ink-400">
                  influence score
                </span>
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center rounded-lg bg-white/85 p-4 backdrop-blur-sm">
                <span className="mb-3 text-center text-sm text-ink-600">
                  See your network impact with Premium or higher
                </span>
                <button
                  onClick={() => navigate('/profile#upgrade')}
                  className="min-h-[44px] rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent-600"
                >
                  Upgrade to Unlock
                </button>
              </div>
            </>
          )}
        </div>

      </div>

      {/* Subscription Management Section */}
      <div className="mb-8">
        <SubscriptionManagement />
      </div>

      {/* Settings Section */}
      <div className="mb-8 rounded-lg border border-paper-200 bg-white p-6 shadow-card sm:p-8">
        <div className="mb-4 font-display text-xs font-medium uppercase tracking-[0.25em] text-accent-600">
          Settings
        </div>
        <div className="flex flex-col gap-6">
          {/* About */}
          <div>
            <h3 className="mb-3 text-base font-medium text-ink-900">
              About
            </h3>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-ink-400">Version</span>
                <span className="text-sm font-medium text-ink-900">{__APP_VERSION__}</span>
              </div>
              <div className="mt-2 flex gap-4">
                <Link
                  to="/terms"
                  className="text-sm text-accent-600 hover:underline"
                >
                  Terms of Service
                </Link>
                <Link
                  to="/privacy"
                  className="text-sm text-accent-600 hover:underline"
                >
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Account Management Section */}
      <div className="rounded-lg border border-paper-200 bg-white p-6 shadow-card sm:p-8">
        <div className="mb-4 font-display text-xs font-medium uppercase tracking-[0.25em] text-accent-600">
          Data &amp; Privacy
        </div>
        {exportSuccess && (
          <div className="mb-6 rounded-sm border border-accent-300 bg-accent-100 p-4 text-sm text-accent-600">
            ✓ Data export completed successfully!
          </div>
        )}
        {exportError && (
          <div className="mb-6 rounded-sm border border-danger-500/30 bg-danger-100 p-4 text-sm text-danger-500">
            {exportError}
          </div>
        )}
        <div className="flex flex-col gap-6">
          <div>
            <h3 className="mb-2 text-base font-medium text-ink-900">Account Data</h3>
            <p className="mb-4 text-sm text-ink-600">
              Download a copy of your verification history and account data, or permanently delete your account.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
              <button
                type="button"
                onClick={handleDataExport}
                disabled={isExporting}
                className="min-h-[44px] w-full rounded-md border border-paper-200 bg-white px-6 py-3 text-sm font-medium text-ink-700 transition-colors hover:bg-paper-50 disabled:cursor-not-allowed disabled:text-ink-300 disabled:hover:bg-white sm:w-auto"
              >
                {isExporting ? 'Exporting...' : 'Export Data'}
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                className="min-h-[44px] w-full rounded-md border border-danger-100 bg-white px-6 py-3 text-sm font-medium text-danger-500 transition-colors hover:bg-danger-100/50 sm:w-auto"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-brand-950/75 p-4 backdrop-blur">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-overlay sm:p-8">
            {/* Modal Header */}
            <div className="mb-6">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-danger-500/20 bg-danger-100">
                <svg className="h-8 w-8 text-danger-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <h2 className="mb-2 text-center text-xl font-semibold text-ink-900">
                Delete Account
              </h2>
              <p className="text-center leading-relaxed text-ink-600">
                Are you sure you want to delete your account? This action cannot be undone.
              </p>
            </div>

            {/* Warning Details */}
            <div className="mb-6 rounded-md border border-danger-500/20 bg-danger-100 p-5">
              <h3 className="mb-3 text-sm font-semibold text-danger-500">
                This will permanently delete:
              </h3>
              <ul className="list-disc space-y-1 pl-5 text-sm text-ink-700">
                <li>All your verification history</li>
                <li>Your account data and preferences</li>
                <li>Non-anonymous community posts you&apos;ve created</li>
                <li>Any subscription or billing information</li>
              </ul>
            </div>

            {/* Active subscription warning */}
            {deletionSubscription &&
             deletionSubscription.status === 'active' &&
             deletionSubscription.tier !== 'free' && (() => {
              const tierName = deletionSubscription.tier.charAt(0).toUpperCase() + deletionSubscription.tier.slice(1)
              const daysRemaining = deletionSubscription.currentPeriodEnd
                ? Math.max(0, Math.ceil(
                    (new Date(deletionSubscription.currentPeriodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                  ))
                : null
              return (
                <div className="mb-6 rounded-sm border border-danger-500/35 bg-danger-100 px-4 py-3.5">
                  <p className="text-[0.8125rem] leading-relaxed text-danger-500">
                    ⚠ You have an active <strong>{tierName}</strong> subscription
                    {daysRemaining !== null && (
                      <> with approximately <strong>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</strong> remaining</>
                    )}.{' '}
                    Deleting your account will permanently cancel it with no refund.
                  </p>
                </div>
              )
            })()}

            {/* Error/Success Messages */}
            {deleteError && (
              <div className="mb-4 rounded-sm border border-danger-500/30 bg-danger-100 p-4 text-sm text-danger-500">
                {deleteError}
              </div>
            )}

            {deleteSuccess && (
              <div className="mb-4 rounded-sm border border-accent-300 bg-accent-100 p-4 text-sm text-accent-600">
                {deleteSuccess}
              </div>
            )}

            {/* Modal Actions */}
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={cancelDeleteAccount}
                disabled={isDeleting}
                className="min-h-[44px] w-full rounded-md border border-paper-200 bg-white px-6 py-3 text-sm font-medium text-ink-700 transition-colors hover:bg-paper-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteAccount}
                disabled={isDeleting || deleteSuccess !== null}
                className={`min-h-[44px] w-full rounded-md px-6 py-3 text-sm font-medium text-white transition-colors sm:w-auto ${
                  deleteSuccess
                    ? 'cursor-not-allowed bg-accent-500 opacity-50'
                    : isDeleting
                    ? 'cursor-not-allowed bg-danger-500 opacity-50'
                    : 'bg-danger-500 hover:bg-danger-500/90'
                }`}
              >
                {isDeleting ? 'Deleting...' : deleteSuccess ? '✓ Deleted' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Cropper Modal */}
      {showImageCropper && tempImageUrl && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-brand-950/75 p-4 backdrop-blur">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-overlay sm:p-8">
            {/* Modal Header */}
            <div className="mb-6">
              <h2 className="mb-2 text-center text-xl font-semibold text-ink-900">
                Position Your Profile Picture
              </h2>
              <p className="text-center text-sm leading-relaxed text-ink-600">
                Drag the image to choose which part appears in your profile circle
              </p>
            </div>

            {/* Image Cropper */}
            <div className="mb-6">
              {/* Large Preview */}
              <div className="relative mx-auto mb-4 h-60 w-60 cursor-move overflow-hidden rounded-full border-[3px] border-brand-950 bg-paper-100 shadow-card">
                {tempImageUrl ? (
                  <img
                    src={tempImageUrl}
                    alt="Profile preview"
                    className="pointer-events-none absolute h-[120%] w-[120%] max-w-none select-none object-cover"
                    style={{
                      left: `-${(cropPosition.x / 100) * 20}%`,
                      top: `-${(cropPosition.y / 100) * 20}%`,
                    }}
                    onDragStart={(e) => e.preventDefault()}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-base text-ink-300">
                    No image
                  </div>
                )}
                <div
                  className="absolute inset-0 cursor-move"
                  onMouseDown={(e) => {
                    if (!tempImageUrl) return
                    e.preventDefault()
                    const startX = e.clientX
                    const startY = e.clientY
                    const startCropX = cropPosition.x
                    const startCropY = cropPosition.y

                    const handleMouseMove = (e: MouseEvent) => {
                      const deltaX = e.clientX - startX
                      const deltaY = e.clientY - startY

                      // Convert pixel movement to percentage - more responsive
                      const newX = Math.max(0, Math.min(100, startCropX + (deltaX / 2)))
                      const newY = Math.max(0, Math.min(100, startCropY + (deltaY / 2)))

                      setCropPosition({ x: newX, y: newY })
                    }

                    const handleMouseUp = () => {
                      document.removeEventListener('mousemove', handleMouseMove)
                      document.removeEventListener('mouseup', handleMouseUp)
                    }

                    document.addEventListener('mousemove', handleMouseMove)
                    document.addEventListener('mouseup', handleMouseUp)
                  }}
                />
              </div>

              {/* Small Preview */}
              <div className="flex items-center justify-center gap-4">
                <span className="text-sm text-ink-400">Preview:</span>
                <div
                  className="h-[60px] w-[60px] rounded-full border-2 border-brand-950 bg-cover shadow-card"
                  style={{
                    backgroundImage: `url(${tempImageUrl})`,
                    backgroundPosition: `${cropPosition.x}% ${cropPosition.y}%`,
                  }}
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleCropCancel}
                className="min-h-[44px] w-full rounded-md border border-paper-200 bg-white px-6 py-3 text-sm font-medium text-ink-700 transition-colors hover:bg-paper-50 sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCropSave}
                disabled={isSavingAvatar}
                className="min-h-[44px] w-full rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:bg-ink-300 sm:w-auto"
              >
                {isSavingAvatar ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProfilePage
